import { documentReady } from "@allwyn/utils/document-ready";

type ColorVariant = "yellow" | "blue";
type LoaderType = "normal" | "above-all";

/**
 * Loader tells user, he has to wait for some ajax operation
 */
export class Loader {
    private static showTimeout: number;
    private static hideTimeout: number;
    private static locks: LoaderType[] = [];
    private static get lockNumber() {
        return this.locks.length;
    }

    public static get isShown() {
        return this.lockNumber > 0;
    }

    static {
        // loader from backend
        if (document.body) {
            this.showLoaderFromBackend();
        } else {
            documentReady(() => {
                this.showLoaderFromBackend();
            });
        }
    }

    static showLoaderFromBackend() {
        if (document.body.classList.contains("loader")) {
            this.locks.push("normal");
        }
    }

    /**
     * Shows loader after some time in miliseconds
     * This is to reduce flickering when ajax actions are super-fast
     *
     * @param showLoaderIn Time in miliseconds, after which, loaded will be shown
     * @param text Title (waiting message) displayed in loader
     */
    static show(showLoaderIn = 30, text?: string, aboveAll = false, color: ColorVariant = "yellow") {
        this.clearTimeout();
        this.locks.push(aboveAll ? "above-all" : "normal");

        this.showTimeout = window.setTimeout(() => {
            this.showNow(text, aboveAll, true, color);
        }, showLoaderIn);
    }

    /**
     * Shows loader right away
     * @param text Title (waiting message) displayed in loader
     */
    static showNow(text?: string, aboveAll = false, counted = false, color: ColorVariant = "yellow") {
        if (!counted) {
            this.locks.push(aboveAll ? "above-all" : "normal");
        }

        if (!environment.isEditMode) {
            const loader = document.getElementById("loader");

            if (!loader) {
                return;
            }

            if (aboveAll) {
                loader.classList.add("loader--above-all");
            } else {
                loader.classList.remove("loader--above-all");
            }
            if (text) {
                loader.querySelector(".loader__text").textContent = text;
                loader.classList.add("loader--with-text");
            } else {
                loader.querySelector(".loader__text").textContent = "";
                loader.classList.remove("loader--with-text");
            }

            if (color === "blue") {
                loader.classList.add("loader--blue");
            }

            document.body.classList.add("loader", "loader--fade-in");
            this.clearTimeout();
        }
    }

    /**
     * Shows loader after delay with z-index of 9999
     * @param text Title (waiting message) displayed in loader
     */
    static showAboveAll(showLoaderIn = 30, text?: string) {
        this.show(showLoaderIn, text, true);
    }

    /**
     * Shows loader right away with z-index of 9999
     * @param text Title (waiting message) displayed in loader
     */
    static showNowAboveAll(text?: string) {
        this.showNow(text, true);
    }

    private static popLock() {
        const lock = this.locks.pop();
        const loader = document.getElementById("loader");
        if (!loader || this.lockNumber === 0) return;

        if (lock === "normal" && this.locks.at(-1) === "above-all") {
            loader.classList.add("loader--above-all");
        } else if (lock === "above-all" && this.locks.at(-1) === "normal") {
            loader.classList.remove("loader--above-all");
        }
    }

    /**
     * Hides loader
     */
    static hide() {
        this.popLock();
        if (this.lockNumber > 0) return;

        this.clearTimeout();
        document.body.classList.remove("loader--fade-in");
        this.hideTimeout = window.setTimeout(() => {
            document.body.classList.remove("loader");
            const loader = document.getElementById("loader");
            loader.querySelector(".loader__text").textContent = "";
            loader.classList.remove("loader--with-text");
        }, 150);
    }

    private static clearTimeout() {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }
}
